import { useCallback, useEffect, useState } from 'react';

import { SAVED_LAND_PLAT_MAP_URL } from '@/constants/urls';
import { County } from '@/endpoints/itasca/county/getCounties';
import { getSavedSelections } from '@/endpoints/itasca/land/saved-selections/getSavedSelections';
import { State } from '@/endpoints/itasca/state/getState';
import { SavedSelection } from '@/models/land';
import { useAuth } from '@/providers/auth-provider';

import { SearchResultType, URLSearchResult } from '../models';

export const useSavedMapsSearch = (
  debouncedInput: string,
  additionalProps?: {
    selectedCounty?: County;
    selectedState?: State;
  }
): {
  saves: URLSearchResult<SavedSelection>[];
  savesSeeMore: number;
  isLoading: boolean;
} => {
  const selectedCounty = additionalProps?.selectedCounty;
  const selectedState = additionalProps?.selectedState;
  const { isAnonymous } = useAuth();
  const [saves, setSaves] = useState<URLSearchResult<SavedSelection>[]>([]);
  const [savesSeeMore, setSavesSeeMore] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setSaves([]);
    setSavesSeeMore(0);
  }, [debouncedInput]);

  const getSaves = useCallback(
    async (signal: AbortSignal) => {
      if (
        (!debouncedInput && !selectedCounty && !selectedState) ||
        isAnonymous
      ) {
        setSaves([]);
        setSavesSeeMore(0);
        return;
      }

      setIsLoading(true);

      const { res, err } = await getSavedSelections(
        {
          location__full_fips: selectedCounty?.full_fips ?? undefined,
          location__state_fips: selectedState?.state_fips ?? undefined,
          page_size: 5,
          searchTerm: debouncedInput,
        },
        signal
      );

      if (err) {
        setSaves([]);
      }

      if (res?.data) {
        setSaves(
          res.data.results.map((x) => ({
            id: `save-${x.id}`,
            label: x.name,
            original: x,
            type: SearchResultType.SAVED_MAP,
            url: `${SAVED_LAND_PLAT_MAP_URL}?id=${x.id}`,
          }))
        );
        setSavesSeeMore(res.data.count);
      }

      setIsLoading(false);
    },
    [debouncedInput, isAnonymous, selectedCounty, selectedState]
  );

  useEffect(() => {
    const abortController = new AbortController();

    getSaves(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [getSaves]);

  return {
    isLoading,
    saves,
    savesSeeMore,
  };
};
